import { matchPath } from "react-router-dom";
import { ROUTE_ALIASES } from "@/i18n/route-aliases";

/**
 * Route → i18n key namespace registry.
 *
 * Data only. The head manager (src/components/seo/RouteHead.tsx) is generic:
 * it takes whatever namespace this registry resolves and reads
 * `<ns>.seoTitle` / `<ns>.seoDescription` (or `<ns>.seoDesc`) from it.
 * Never add page-specific behaviour to the manager — add a row here.
 *
 * Patterns are relative to `/:locale/` (no leading slash), exactly as mounted
 * in App.tsx.
 */
export type HeadRoute = {
  pattern: string;
  /** i18n namespace holding seoTitle / seoDescription. */
  ns?: string;
  /** Section label key used when the namespace has no dedicated copy. */
  section?: string;
  noindex?: boolean;
};

export const HEAD_ROUTES: HeadRoute[] = [
  { pattern: "", ns: "marketing.landing", section: "home" },
  // Marketing
  { pattern: "catalog", ns: "marketing.catalog", section: "catalog" },
  { pattern: "catalog/:slug", ns: "marketing.catalog", section: "catalog" },
  { pattern: "bootcamps", ns: "marketing.catalog", section: "catalog" },
  { pattern: "bootcamps/:slug", ns: "marketing.catalog", section: "catalog" },
  { pattern: "courses", ns: "marketing.catalog", section: "catalog" },
  { pattern: "courses/:slug", ns: "marketing.catalog", section: "catalog" },
  { pattern: "tracks/:slug", section: "tracks" },
  { pattern: "pricing", ns: "marketing.pricing", section: "pricing" },
  { pattern: "experts/pricing", ns: "marketing.pricing", section: "pricing" },
  { pattern: "about", ns: "marketing.about", section: "about" },
  { pattern: "enterprise", ns: "marketing.enterprise", section: "enterprise" },
  { pattern: "teams", ns: "marketing.teams", section: "teams" },
  { pattern: "faq", ns: "marketing.faq", section: "faq" },
  { pattern: "blog", ns: "marketing.blog", section: "blog" },
  { pattern: "blog/:slug", ns: "marketing.blog", section: "blog" },
  { pattern: "contact", ns: "marketing.contact", section: "contact" },
  { pattern: "case-studies", ns: "caseStudies", section: "caseStudies" },
  { pattern: "case-studies/:slug", ns: "caseStudies", section: "caseStudies" },
  { pattern: "careers", ns: "careers", section: "careers" },
  { pattern: "careers/:slug", ns: "careers.detail", section: "careers" },
  { pattern: "diagnostic", ns: "onboarding.diagnostic", section: "diagnostic" },
  { pattern: "business-validator", section: "validator" },
  { pattern: "business-validator/unlock", section: "validator" },
  { pattern: "search", section: "search", noindex: true },
  { pattern: "500", ns: "errors.server", section: "error", noindex: true },
  // Services & pillars
  { pattern: "services", ns: "services.index", section: "services" },
  { pattern: "services/hub", ns: "services.hub", section: "services", noindex: true },
  { pattern: "services/microservices", ns: "services.micro", section: "services" },
  { pattern: "services/microservices/:slug", ns: "services.micro", section: "services" },
  { pattern: "services/coaching", ns: "services.coaching", section: "services" },
  { pattern: "services/coaching/:providerId", ns: "services.coaching", section: "services" },
  { pattern: "services/retainers", ns: "services.retainer", section: "services" },
  { pattern: "services/investor-readiness", ns: "services.ir", section: "services" },
  { pattern: "mentoring", section: "mentoring" },
  { pattern: "fractional", ns: "pages.fractional", section: "fractional" },
  { pattern: "fractional/:slug", ns: "pages.fractional", section: "fractional" },
  { pattern: "mentors", ns: "mentors.directory", section: "mentors" },
  { pattern: "mentors/:slug", ns: "mentors.directory", section: "mentors" },
  { pattern: "coaches", section: "coaches" },
  { pattern: "providers", section: "providers" },
  { pattern: "providers/:id", section: "providers" },
  { pattern: "marketplace", section: "marketplace" },
  { pattern: "downloads", section: "downloads" },
  { pattern: "downloads/:slug", section: "downloads" },
  { pattern: "support", ns: "pages.supportPub", section: "support" },
  { pattern: "apply", ns: "apply", section: "apply" },
  { pattern: "apply/form", ns: "apply.form", section: "apply" },
  { pattern: "cert/:code", section: "certificate" },
  { pattern: "u/:userId", section: "profile" },
  // Legal
  { pattern: "legal/terms", ns: "legal.terms", section: "legal" },
  { pattern: "legal/privacy", ns: "legal.privacy", section: "legal" },
  { pattern: "legal/refund-policy", section: "legal" },
  { pattern: "legal/dpa", section: "legal" },
  // Auth
  { pattern: "login", ns: "auth.login", section: "auth", noindex: true },
  { pattern: "signup", ns: "auth.signup", section: "auth", noindex: true },
  { pattern: "auth/forgot-password", section: "auth", noindex: true },
  { pattern: "auth/reset-password", section: "auth", noindex: true },
  // Onboarding
  { pattern: "onboarding/persona", ns: "onboarding.persona", section: "onboarding", noindex: true },
  { pattern: "onboarding/tracks", ns: "onboarding.tracks", section: "onboarding", noindex: true },
  { pattern: "onboarding/diagnostic", ns: "onboarding.diagnostic", section: "onboarding", noindex: true },
  { pattern: "onboarding/complete", ns: "onboarding.complete", section: "onboarding", noindex: true },
  { pattern: "onboarding/role", ns: "onboarding.role", section: "onboarding", noindex: true },
  // Provider workspace
  { pattern: "provider", ns: "provider.dashboard", section: "provider", noindex: true },
  { pattern: "provider/profile", ns: "provider.profile", section: "provider", noindex: true },
  { pattern: "provider/availability", ns: "provider.availability", section: "provider", noindex: true },
  { pattern: "provider/earnings", ns: "provider.earnings", section: "provider", noindex: true },
  // Community
  { pattern: "community", ns: "pages.community", section: "community", noindex: true },
  // Newsletter
  { pattern: "newsletter/confirmed", section: "newsletter", noindex: true },
  { pattern: "newsletter/unsubscribed", section: "newsletter", noindex: true },
  { pattern: "newsletter/preferences", section: "newsletter", noindex: true },
  // Legacy / vanity paths that redirect — they still paint one frame.
  { pattern: "plans", ns: "marketing.pricing", section: "pricing" },
  { pattern: "coaching", ns: "services.coaching", section: "services" },
  { pattern: "microservices", ns: "services.micro", section: "services" },
  { pattern: "executives", ns: "pages.fractional", section: "fractional" },
  { pattern: "contact-us", ns: "marketing.contact", section: "contact" },
  { pattern: "blogs", ns: "marketing.blog", section: "blog" },
  { pattern: "knowledge-base", ns: "marketing.faq", section: "faq" },
  { pattern: "knowledge-base-topicks", ns: "marketing.faq", section: "faq" },
  { pattern: "auth", ns: "auth.signup", section: "auth", noindex: true },
  { pattern: "validateur-business/deverrouiller", section: "validator" },
];

/**
 * Localized slug aliases (e.g. /fr/bootcamps, /es/estudios-de-caso) inherit the
 * canonical route's head config — derived, never hand-maintained.
 */
const ALIAS_ROUTES: HeadRoute[] = HEAD_ROUTES.flatMap((r) => {
  const [head, ...tail] = r.pattern.split("/");
  const map = (ROUTE_ALIASES as Record<string, Record<string, string>>)[head];
  if (!map) return [];
  return Array.from(new Set(Object.values(map))).map((alias) => ({
    ...r,
    pattern: [alias, ...tail].join("/"),
  }));
});

/**
 * Prefix fallbacks for everything not listed above (app surfaces, admin,
 * checkout, settings…). First match wins; order from most to least specific.
 */
export const SECTION_FALLBACKS: { prefix: string; section: string; noindex?: boolean }[] = [
  { prefix: "admin", section: "admin", noindex: true },
  { prefix: "provider", section: "provider", noindex: true },
  { prefix: "community", section: "community", noindex: true },
  { prefix: "settings", section: "settings", noindex: true },
  { prefix: "checkout", section: "checkout", noindex: true },
  { prefix: "downloads", section: "downloads" },
  { prefix: "marketplace", section: "marketplace" },
  { prefix: "learn", section: "learn", noindex: true },
  { prefix: "mentoring", section: "mentoring" },
  { prefix: "mentor", section: "mentor", noindex: true },
  { prefix: "strategist", section: "mentor", noindex: true },
  { prefix: "coach", section: "coach", noindex: true },
  { prefix: "actions", section: "coach", noindex: true },
  { prefix: "onboarding", section: "onboarding", noindex: true },
  { prefix: "messages", section: "messages", noindex: true },
  { prefix: "notifications", section: "notifications", noindex: true },
  { prefix: "certificates", section: "certificate", noindex: true },
  { prefix: "resources", section: "resources", noindex: true },
  { prefix: "library", section: "library", noindex: true },
  { prefix: "support", section: "support", noindex: true },
  { prefix: "booking", section: "booking", noindex: true },
  { prefix: "dashboard", section: "dashboard", noindex: true },
  { prefix: "outcomes", section: "dashboard", noindex: true },
  { prefix: "apply", section: "apply" },
  { prefix: "services", section: "services" },
  { prefix: "design-system", section: "app", noindex: true },
  { prefix: "dev", section: "app", noindex: true },
  { prefix: "app", section: "app", noindex: true },
];

export type ResolvedHeadRoute = {
  ns?: string;
  section: string;
  noindex: boolean;
  /** How the config was found — "none" means the route has no registry coverage. */
  matched: "route" | "prefix" | "none";
};

/** Resolve a locale-stripped path (e.g. "/services/coaching") to its head config. */
export function resolveHeadRoute(relPath: string): ResolvedHeadRoute {
  const path = relPath.startsWith("/") ? relPath : `/${relPath}`;
  for (const r of [...HEAD_ROUTES, ...ALIAS_ROUTES]) {
    const p = `/${r.pattern}`.replace(/\/+$/, "") || "/";
    if (matchPath({ path: p, end: true }, path)) {
      return { ns: r.ns, section: r.section ?? "app", noindex: !!r.noindex, matched: "route" };
    }
  }
  const first = path.split("/").filter(Boolean)[0] ?? "";
  const fb = SECTION_FALLBACKS.find((f) => f.prefix === first);
  if (fb) return { section: fb.section, noindex: !!fb.noindex, matched: "prefix" };
  return { section: "app", noindex: false, matched: "none" };
}
